import React, { useState } from 'react';
import { firestoreService } from '../shared/services/firestoreService';
import { getBackendService, USE_REAL_API } from '../shared/services/apiConfig';
import { Button, Input, Badge } from '../shared/design';
import AtableLogo from '../shared/design/components/AtableLogo';
import RestaurateurSignupForm from './RestaurateurSignupForm';

const USE_FIREBASE = import.meta.env.VITE_USE_FIREBASE === 'true';

type AccountType = 'client' | 'restaurateur';

const ACCOUNT_TYPES: { value: AccountType; label: string; description: string }[] = [
    { value: 'client', label: 'Je suis client', description: 'Fidélité et récompenses dans l\'app mobile' },
    { value: 'restaurateur', label: 'Je suis restaurateur', description: 'Demande d\'accès à l\'espace pro' },
];

const AuthScreen: React.FC = () => {
    const [view, setView] = useState<'login' | 'register'>('login');
    const [accountType, setAccountType] = useState<AccountType>('client');
    const isRestaurateurSignup = view === 'register' && accountType === 'restaurateur';
    // Demande restaurateur en cours d'envoi : onglets et choix du type de compte figés.
    const [restaurateurBusy, setRestaurateurBusy] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Demo credentials
    const DEMO_CREDENTIALS = [
        { email: 'client@atable.test', role: 'Client', icon: 'C', color: 'primary' as const },
        { email: 'resto@atable.test', role: 'Restaurateur', icon: 'R', color: 'secondary' as const },
        { email: 'admin@atable.test', role: 'Admin', icon: 'A', color: 'info' as const },
    ];

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        try {
            if (USE_FIREBASE) {
                await firestoreService.login(email, password);
            } else {
                // Mode démo (mock) ou API NestJS : on passe par le backend sélectionné.
                const backend = await getBackendService();
                await (backend as any).login(email, password);
            }
        } catch (err: any) {
            setError(err.message || 'Erreur de connexion');
            setIsLoading(false);
        }
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        try {
            const [first, last] = name.split(' ');
            if (USE_FIREBASE) {
                await firestoreService.registerClient(email, password, first || name, last || '');
            } else {
                const backend = await getBackendService();
                if (USE_REAL_API) await (backend as any).registerClient(email, password, first || name, last || '');
                else await (backend as any).registerClient(email, first || name, last || ''); // mock : pas de mot de passe
            }
        } catch (err: any) {
            setError(err.message || 'Erreur d\'inscription');
            setIsLoading(false);
        }
    };

    const fillDemo = async (emailStr: string) => {
        setEmail(emailStr);
        setPassword('Demo1234!');
    };

    return (
        <div className="relative min-h-screen bg-cream flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 animate-fade-in">
            {/* Halos pêche / rose du site */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
                <div className="absolute -top-40 -right-40 w-[28rem] h-[28rem] bg-peach-200 rounded-full blur-3xl opacity-60"></div>
                <div className="absolute -bottom-48 -left-40 w-[28rem] h-[28rem] bg-pink-200 rounded-full blur-3xl opacity-50"></div>
            </div>

            <div className="relative z-10 sm:mx-auto sm:w-full sm:max-w-md">
                {/* Logo officiel atable (lockup wordmark + tagline) */}
                <div className="flex justify-center mb-8">
                    <AtableLogo variant="lockup" height={120} />
                </div>

                <div className="text-center">
                    <h1 className="page-title mb-2">
                        {view === 'login' ? 'Connexion' : 'Créer un compte'}
                    </h1>
                    <p className="text-base text-gray-600">
                        {view === 'login' ? 'Connectez-vous à votre espace'
                            : isRestaurateurSignup ? 'Demandez votre accès professionnel'
                                : 'Rejoignez la révolution fidélité'}
                    </p>
                </div>
            </div>

            <div className="relative z-10 mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-6 sm:px-8 shadow-lg rounded-3xl border border-black/[0.06]">
                    {/* Tabs */}
                    <div className="flex bg-cream-200 p-1 rounded-full mb-8">
                        <button
                            onClick={() => setView('login')}
                            disabled={restaurateurBusy}
                            className={`flex-1 h-10 px-4 text-sm font-semibold rounded-full transition-all ${view === 'login'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                                }`}
                        >
                            Connexion
                        </button>
                        <button
                            onClick={() => setView('register')}
                            disabled={restaurateurBusy}
                            className={`flex-1 h-10 px-4 text-sm font-semibold rounded-full transition-all ${view === 'register'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                                }`}
                        >
                            Inscription
                        </button>
                    </div>

                    {/* Type de compte (inscription) */}
                    {view === 'register' && (
                        <fieldset className="mb-6">
                            <legend className="text-sm font-medium text-gray-700 mb-2">Vous êtes</legend>
                            <div className="grid grid-cols-2 gap-3">
                                {ACCOUNT_TYPES.map((type) => (
                                    <label key={type.value} className="relative cursor-pointer">
                                        <input
                                            type="radio"
                                            name="accountType"
                                            value={type.value}
                                            checked={accountType === type.value}
                                            onChange={() => { setAccountType(type.value); setError(null); }}
                                            disabled={restaurateurBusy}
                                            className="peer sr-only"
                                        />
                                        <span className="block h-full p-3 rounded-2xl border-2 border-gray-200 bg-white transition-all hover:border-brand-300 peer-checked:border-brand-500 peer-checked:bg-brand-50 peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-2">
                                            <span className="block text-sm font-semibold text-gray-900">{type.label}</span>
                                            <span className="block text-xs text-gray-500 mt-0.5">{type.description}</span>
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </fieldset>
                    )}

                    {isRestaurateurSignup && <RestaurateurSignupForm onBusyChange={setRestaurateurBusy} />}

                    {/* Form */}
                    {!isRestaurateurSignup && <form className="space-y-5" onSubmit={view === 'login' ? handleLogin : handleRegister}>
                        {view === 'register' && (
                            <Input
                                label="Nom complet"
                                type="text"
                                required
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="Jean Dupont"
                                fullWidth
                                leftIcon={
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                }
                            />
                        )}

                        <Input
                            label="Adresse email"
                            type="email"
                            required
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder="vous@exemple.com"
                            fullWidth
                            leftIcon={
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            }
                        />

                        <Input
                            label="Mot de passe"
                            type="password"
                            required
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            placeholder="••••••••"
                            fullWidth
                            leftIcon={
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            }
                        />

                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2">
                                <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            fullWidth
                            loading={isLoading}
                        >
                            {view === 'login' ? 'Se connecter' : "S'inscrire"}
                        </Button>
                    </form>}

                    {/* Quick Access Demo */}
                    {view === 'login' && !USE_FIREBASE && (
                        <div className="mt-8">
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-200" />
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-4 bg-white text-gray-500 font-medium">Comptes de démonstration</span>
                                </div>
                            </div>

                            <div className="mt-6 space-y-3">
                                {DEMO_CREDENTIALS.map((cred) => (
                                    <button
                                        key={cred.email}
                                        onClick={() => fillDemo(cred.email)}
                                        className="w-full flex items-center justify-between p-3.5 border border-gray-200 rounded-2xl hover:border-brand-300 hover:bg-cream transition-all group"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-cream-200 text-brand-600 font-display rounded-full flex items-center justify-center text-base group-hover:scale-105 transition-transform">
                                                {cred.icon}
                                            </div>
                                            <div className="text-left">
                                                <p className="font-semibold text-gray-900">{cred.role}</p>
                                                <p className="text-xs text-gray-500">{cred.email}</p>
                                            </div>
                                        </div>
                                        <Badge variant={cred.color} size="sm">Démo</Badge>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <p className="mt-6 text-center text-sm text-gray-500">
                    En vous connectant, vous acceptez nos{' '}
                    <a href="#" className="font-medium text-brand-600 hover:text-brand-500 underline-offset-2 hover:underline">Conditions d'utilisation</a>
                </p>
            </div>
        </div>
    );
};

export default AuthScreen;
